import qs from 'qs';
import * as React from 'react';
import { withRouter } from 'react-router-dom';
import { stripHtml } from "string-strip-html";

import {
  fileDocumentIcon,
  sortSkillGroupsByQuestionNumbers,
} from './shared';
import {
  SkillGroupSummary
} from './interfaces'
import ReportHeader from './reportHeader';

import { requestGet } from '../../../../../../modules/request/index';
import {
  DataTable,
  Spinner,
  Tooltip,
} from '../../../../../Shared/index';
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths';

interface Question {
  instructions: string;
  prompt: string;
  score: number;
  question_id: number;
  question_uid: string;
  question_number: number;
}

const AVERAGE_FONT_WIDTH = 6.5
const ALLOTTED_WIDTH = 550

const TABLE_WIDTH_PROPS = {
  additionalWidthRestrictions: 128, // container padding and margin plus the 32px of padding on the table itself
}

const scoreToClassName = (score) => {
  if (score >= 83) { return 'frequently-demonstrated-skill' }
  if (score >= 32) { return 'sometimes-demonstrated-skill' }
  return 'rarely-demonstrated-skill'
}

const DirectionsAndPrompt = ({ directions, prompt, onMobile, }) => {
  function textOrTooltip(text) {
    if (onMobile) {
      return <p dangerouslySetInnerHTML={{ __html: text }} />
    }

    return <p>{stripHtml(text).result}</p>
  }

  return (
    <div className="directions-and-prompt">
      <div className="directions">
        <span>Directions</span>
        {textOrTooltip(directions)}
      </div>
      <div className="prompt">
        <span>Prompt</span>
        {textOrTooltip(prompt)}
      </div>
    </div>
  )
}

export const Questions = ({ passedQuestions, match, mobileNavigation, location, passedSkillGroupSummaries}) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const [loading, setLoading] = React.useState<boolean>(!(passedQuestions && passedSkillGroupSummaries));
  const [questions, setQuestions] = React.useState<Question[]>(passedQuestions || []);
  const [skillGroupSummaries, setSkillGroupSummaries] = React.useState<SkillGroupSummary[]>(passedSkillGroupSummaries || []);
  const [completedStudentCount, setCompletedStudentCount] = React.useState(null)

  const { activityId, classroomId, } = match.params
  const unitId = qs.parse(location.search.replace('?', '')).unit
  const unitQueryString = unitId ? `?unit_id=${unitId}` : ''

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 80, minWidth: 55 },
      { width: 120, minWidth: 95 },
      { width: 620, minWidth: 200 }
    ],
    containerRef,
    maxTableWidth: 820,
    minTableWidth: 300,
    ...TABLE_WIDTH_PROPS
  });

  const desktopHeaders = [
    {
      name: 'Question',
      attribute: 'questionNumber',
      width: columnWidths['80w-55min'],
      textAlign: 'center',
      headerClassName: 'question-number-header',
      rowSectionClassName: 'question-number-section',
      renderCellsAsHeaders: true
    },
    {
      name: 'Activity Scores',
      attribute: 'scoreElement',
      width: columnWidths['120w-95min'],
      noTooltip: true,
      rowSectionClassName: 'score-section',
      headerClassName: 'score-header'
    },
    {
      name: '',
      attribute: 'directionsAndPrompt',
      width: columnWidths['620w-200min'],
      noTooltip: true,
      rowSectionClassName: 'directions-and-prompt-section',
      headerClassName: 'directions-and-prompt-header'
    }
  ]

  React.useEffect(() => {
    getQuestions()
    getResults()
  }, [])

  React.useEffect(() => {
    // only false in test environment
    if (!(passedQuestions && passedSkillGroupSummaries)) {
      setLoading(true)
    }
    getQuestions()
    getResults()
  }, [activityId, classroomId, unitId])

  React.useEffect(() => {
    if (questions.length && skillGroupSummaries.length && Number.isInteger(completedStudentCount)) {
      setLoading(false)
    }
  }, [questions, skillGroupSummaries, completedStudentCount])

  function getQuestions() {
    requestGet(`/teachers/progress_reports/question_view/classroom/${classroomId}/activity/${activityId}${unitQueryString}`,
      (data) => {
        setQuestions(data.data);
      }
    )
  }

  function getResults() {
    requestGet(`/teachers/progress_reports/diagnostic_results_summary?activity_id=${activityId}&classroom_id=${classroomId}${unitQueryString}`,
      (data) => {
        setCompletedStudentCount(data.student_results.filter(sr => sr.skill_groups).length)
        setSkillGroupSummaries(data.skill_group_summaries);
      }
    )
  }

  if (loading) { return <Spinner /> }

  const sortedSkillGroups = sortSkillGroupsByQuestionNumbers(skillGroupSummaries, questions)

  const skillGroups = sortedSkillGroups.map(skillGroup => {
    const filteredQuestions = questions.filter(q => skillGroup.question_uids.includes(q.question_uid)).sort((a, b) => a.question_number - b.question_number);
    const proficiencyScoresSum: any = Object.values(skillGroup.proficiency_scores_by_student).reduce((a: number, b: number) => a + b, 0)
    const percentage = Math.round((proficiencyScoresSum/completedStudentCount) * 100)

    const desktopRows = filteredQuestions.map(question => {
      const { instructions, prompt, question_id, score, } = question
      return {
        id: question_id,
        questionNumber: question_id,
        directionsAndPrompt: <DirectionsAndPrompt directions={instructions} onMobile={false} prompt={prompt} />,
        score,
        scoreElement: score || score === 0 ? <span className={scoreToClassName(score)}>{score}%</span> : null
      }
    })

    return (
      <section className="skill-group-section" id={skillGroup.id} key={skillGroup.id} >
        <div className="skill-group-section-header">
          <h2>
            <span>{skillGroup.name}</span>
            {completedStudentCount ? (
              <div>
                <span>{percentage}%</span>
                <span className="subheader">Average score</span>
              </div>
            ) : null}
          </h2>
        </div>
        <div className="data-table-wrapper">
          <DataTable
            className=""
            headers={desktopHeaders}
            rows={desktopRows}
          />
        </div>
      </section>
    );
  });

  return (
    <main className="questions-index-container" ref={containerRef}>
      <ReportHeader
        guideLink={<a className="quill-button small grey outlined focus-on-light" href="https://support.quill.org/en/articles/5698219-how-do-i-read-the-questions-analysis-report" rel="noopener noreferrer" target="_blank">{fileDocumentIcon}<span>Guide</span></a>}
        pageName="Questions Analysis"
        subHeader="See class performance by question"
      />
      {mobileNavigation}
      {skillGroups}
    </main>
  )
}

export default withRouter(Questions)
